import { useState, useRef, useCallback } from "react";
import { supabase } from "../supabaseClient";

/* ─── PALETTE YOMA ─────────────────────────────────── */
const C = {
  bg: "#F8F4EE",
  bgCard: "#FFFFFF",
  bgDeep: "#F2EBE0",
  taupe: "#8B7361",
  taupeLight: "#C4B5A5",
  taupeDeep: "#3D2E20",
  beige: "#EDE3D5",
  gold: "#C09A5B",
  goldLight: "#E8D4B0",
  goldDeep: "#9A7A3A",
  text: "#2E2418",
  muted: "#9A8778",
  border: "#DDD0C0",
  success: "#5C8A5C",
  successLight: "#EAF2EA",
  alert: "#B85C3A",
  alertLight: "#FAF0EB",
  locked: "#C8BDB5",
  lockedBg: "#F5F0EB",
  info: "#4A7A9B",
  infoLight: "#EBF2F8",
  white: "#FFFFFF",
};

const Styles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,700;1,400;1,500&family=DM+Sans:wght@300;400;500;600&display=swap');
    *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
    body{background:${C.bg};font-family:'DM Sans',sans-serif}
    input,textarea,select{font-family:'DM Sans',sans-serif}
    ::-webkit-scrollbar{width:4px}
    ::-webkit-scrollbar-track{background:${C.beige}}
    ::-webkit-scrollbar-thumb{background:${C.taupeLight};border-radius:2px}
    @keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
    @keyframes shimmer{0%,100%{opacity:.4}50%{opacity:1}}
    .step-content{animation:fadeIn .35s ease}
    .pulse{animation:shimmer 1.4s infinite}
  `}</style>
);

const ETAPES = [
  { id: 0, code: "0", label: "Audit & Stratégie", short: "Audit", subtitle: "Plans · Analyse · Budget", icon: "◈", color: C.gold, livrable: "Rapport d'analyse + alertes", validationLabel: "Valider l'Audit — Passer à la Préparation", description: "Collecte des plans, analyse des incohérences, points critiques et écart budget réel." },
  { id: 1, code: "1", label: "Préparation Opérationnelle", short: "Prép.", subtitle: "Gantt · Entreprises · Lots", icon: "◉", color: "#7A9B8A", livrable: "Planning officiel + Dossier technique", validationLabel: "Valider la Préparation — Lancer le chantier", description: "Planning Gantt, entreprises, lots, validation matériaux et plan d'installation chantier." },
  { id: 2, code: "2", label: "Lancement Chantier", short: "Lancement", subtitle: "Réunion · Règles", icon: "◇", color: "#8A7A9B", livrable: "CR réunion signé", validationLabel: "Valider le Lancement — Pilotage hebdo", description: "Réunion de lancement, participants, points présentés et règles de chantier." },
  { id: 3, code: "3", label: "Pilotage Hebdomadaire", short: "Pilotage", subtitle: "Visites · CR", icon: "◎", color: "#9B8A5A", livrable: "CR + tableau de suivi", validationLabel: "Valider le Pilotage — Coordination technique", description: "Fiches de visite : avancement, photos, blocages, comparatif prévu/réel." },
  { id: 4, code: "4", label: "Coordination Technique", short: "Coord.", subtitle: "OPC · Interfaces", icon: "◐", color: "#5A8A9B", livrable: "CR coordination + décisions actées", validationLabel: "Valider la Coordination — Gestion des modifications", description: "Réunions OPC, interfaces techniques et arbitrages." },
  { id: 5, code: "5", label: "Gestion des Modifications", short: "Modifs", subtitle: "OS · Traçabilité", icon: "▣", color: C.taupe, livrable: "OS validé + traçabilité", validationLabel: "Valider les Modifications — Gestion des retards", description: "Demandes client, impacts coût/délai, statuts et ordres de service." },
  { id: 6, code: "6", label: "Gestion des Retards", short: "Retards", subtitle: "Plans correctifs", icon: "◫", color: C.alert, livrable: "Fiche retard + plan correctif", validationLabel: "Valider les Retards — Pré-réception OPR", description: "Retards par entreprise, causes, impacts et plans correctifs." },
  { id: 7, code: "7", label: "Pré-réception (OPR)", short: "OPR", subtitle: "Zones · Réserves", icon: "▤", color: "#7A6B9B", livrable: "Liste OPR complète", validationLabel: "Valider l'OPR — Réception", description: "Inspection par zone : réserves, photos, délais de correction." },
  { id: 8, code: "8", label: "Réception", short: "Réception", subtitle: "PV · Réserves", icon: "•", color: C.success, livrable: "PV de réception signé", validationLabel: "Valider la Réception — Clôture & SAV", description: "Date de réception, parties présentes, levée des réserves OPR." },
  { id: 9, code: "9", label: "Clôture & SAV", short: "Clôture", subtitle: "DOE · GPA", icon: "◆", color: C.goldDeep, livrable: "DOE complet", validationLabel: "Clôturer la mission OPC", description: "Checklist DOE, garanties, GPA et observations finales." },
];

async function callAI(system, user, images = []) {
  const { data, error } = await supabase.functions.invoke("generate-ai", {
    body: { system, user, images },
  });
  if (error) throw error;
  return data?.data?.content?.[0]?.text || "Erreur génération";
}

const Label = ({ children, required }) => (
  <div style={{ fontFamily: "'DM Sans'", fontSize: "10px", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: C.muted, marginBottom: "6px" }}>
    {children}
    {required && <span style={{ color: C.alert, marginLeft: "3px" }}>*</span>}
  </div>
);

const Input = ({ value, onChange, placeholder, multiline, rows = 3, type, style: s }) => {
  const base = {
    width: "100%",
    border: `1px solid ${C.border}`,
    borderRadius: "3px",
    padding: "9px 12px",
    background: C.bg,
    color: C.text,
    outline: "none",
    fontSize: "13px",
    fontFamily: "'DM Sans'",
    transition: "border-color .2s",
    ...s,
  };
  return multiline ? (
    <textarea rows={rows} style={base} value={value} onChange={onChange} placeholder={placeholder} onFocus={(e) => (e.target.style.borderColor = C.gold)} onBlur={(e) => (e.target.style.borderColor = C.border)} />
  ) : (
    <input type={type || "text"} style={base} value={value} onChange={onChange} placeholder={placeholder} onFocus={(e) => (e.target.style.borderColor = C.gold)} onBlur={(e) => (e.target.style.borderColor = C.border)} />
  );
};

const Sel = ({ value, onChange, options }) => (
  <select value={value} onChange={onChange} style={{ width: "100%", border: `1px solid ${C.border}`, borderRadius: "3px", padding: "9px 12px", background: C.bg, color: C.text, fontSize: "13px", fontFamily: "'DM Sans'", outline: "none" }}>
    {options.map((o) => (
      <option key={o.value || o} value={o.value || o}>
        {o.label || o}
      </option>
    ))}
  </select>
);

const Card = ({ children, style: s }) => <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: "4px", padding: "24px", ...s }}>{children}</div>;

const Grid = ({ cols = 2, children, gap = "16px" }) => <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap }}>{children}</div>;

const Field = ({ label, children, required }) => (
  <div><Label required={required}>{label}</Label>
    {children}
  </div>
);

const SecTitle = ({ children, sub }) => (
  <div style={{ marginBottom: "20px" }}><div style={{ fontFamily: "'Playfair Display', serif", fontSize: "20px", color: C.taupeDeep, fontWeight: 500 }}>{children}</div>
    {sub && <div style={{ fontFamily: "'DM Sans'", fontSize: "11px", color: C.muted, marginTop: "4px", letterSpacing: "0.05em" }}>{sub}</div>}
  </div>
);

const AIBox = ({ content, loading, title = "Analyse IA — YOMA DESIGN" }) => (
  <div style={{ background: `linear-gradient(135deg, ${C.beige}, #F8F0E8)`, border: `1px solid ${C.goldLight}`, borderLeft: `4px solid ${C.gold}`, borderRadius: "3px", padding: "22px", marginTop: "20px" }}><div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}><div style={{ width: "6px", height: "6px", borderRadius: "50%", background: C.gold }} /><span style={{ fontFamily: "'DM Sans'", fontSize: "9px", fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: C.goldDeep }}>{title}</span></div>
    {loading ? (
      <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
        {[0, 1, 2].map((i) => (
          <div key={i} className="pulse" style={{ width: "7px", height: "7px", borderRadius: "50%", background: C.gold, animationDelay: `${i * 0.2}s` }} />
        ))}
        <span style={{ fontFamily: "'DM Sans'", fontSize: "12px", color: C.muted, marginLeft: "6px" }}>Génération en cours…</span></div>
    ) : (
      <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "14px", color: C.taupeDeep, lineHeight: 1.9, whiteSpace: "pre-wrap" }}>{content}</div>
    )}
  </div>
);

const Btn = ({ children, onClick, variant = "primary", disabled, small, style: s }) => {
  const vars = {
    primary: { background: C.taupeDeep, color: C.white },
    gold: { background: C.gold, color: C.white },
    ghost: { background: "transparent", color: C.taupe, border: `1px solid ${C.border}` },
    success: { background: C.success, color: C.white },
    danger: { background: C.alert, color: C.white },
    locked: { background: C.lockedBg, color: C.locked, border: `1px solid ${C.border}`, cursor: "not-allowed" },
  };
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      style={{
        fontFamily: "'DM Sans'",
        fontWeight: 600,
        letterSpacing: "0.08em",
        fontSize: small ? "10px" : "11px",
        padding: small ? "7px 14px" : "11px 24px",
        border: "none",
        borderRadius: "3px",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
        transition: "all .2s",
        textTransform: "uppercase",
        ...vars[variant],
        ...s,
      }}
    >
      {children}
    </button>
  );
};

const Badge = ({ children, color = C.gold }) => (
  <span
    style={{
      background: color + "20",
      color,
      border: `1px solid ${color}50`,
      borderRadius: "2px",
      padding: "2px 8px",
      fontFamily: "'DM Sans'",
      fontSize: "9px",
      fontWeight: 600,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
    }}
  >
    {children}
  </span>
);

const LivrablerBadge = ({ code, label, generated }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 14px", background: generated ? C.successLight : C.bgDeep, border: `1px solid ${generated ? C.success + "50" : C.border}`, borderRadius: "3px" }}><div style={{ fontFamily: "'Playfair Display'", fontSize: "18px", color: generated ? C.success : C.taupeLight, fontWeight: 700 }}>{code}</div><div><div style={{ fontFamily: "'DM Sans'", fontSize: "11px", fontWeight: 600, color: generated ? C.success : C.muted }}>{label}</div><div style={{ fontFamily: "'DM Sans'", fontSize: "10px", color: C.muted }}>{generated ? "✓ Généré" : "En attente"}</div></div></div>
);

function UploadZone({ label, files, onFiles, accept = "image/*", multiple = true }) {
  const ref = useRef();
  const handle = useCallback(
    (fl) => {
      const ps = [...fl].map(
        (f) =>
          new Promise((res) => {
            const r = new FileReader();
            r.onload = () => res({ id: Date.now() + Math.random(), name: f.name, url: r.result, type: f.type, data: r.result.split(",")[1] });
            r.readAsDataURL(f);
          })
      );
      Promise.all(ps).then((imgs) => onFiles((prev) => [...(prev || []), ...imgs]));
    },
    [onFiles]
  );

  return (
    <div><Label>{label}</Label><div
        style={{ border: `2px dashed ${C.border}`, borderRadius: "3px", padding: "20px", textAlign: "center", cursor: "pointer", background: C.bg, transition: "border-color .2s" }}
        onClick={() => ref.current.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          handle(e.dataTransfer.files);
        }}
        onMouseEnter={(e) => (e.currentTarget.style.borderColor = C.gold)}
        onMouseLeave={(e) => (e.currentTarget.style.borderColor = C.border)}
      ><div style={{ fontFamily: "'Playfair Display'", fontSize: "22px", color: C.taupeLight }}>◎</div><div style={{ fontFamily: "'DM Sans'", fontSize: "11px", color: C.muted, marginTop: "6px" }}>Glisser ou cliquer — {accept}</div><input ref={ref} type="file" accept={accept} multiple={multiple} style={{ display: "none" }} onChange={(e) => handle(e.target.files)} /></div>
      {files?.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "10px" }}>
          {files.map((f) => (
            <div key={f.id} style={{ position: "relative", borderRadius: "3px", overflow: "hidden", border: `1px solid ${C.border}` }}>
              {f.type?.startsWith("image") ? (
                <img src={f.url} alt={f.name} style={{ width: "80px", height: "60px", objectFit: "cover", display: "block" }} />
              ) : (
                <div style={{ width: "80px", height: "60px", background: C.bgDeep, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", color: C.muted, padding: "4px", textAlign: "center" }}>
                  {f.name}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ValidationModal({ etape, onConfirm, onCancel }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(30,21,16,.75)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}><div style={{ background: C.bgCard, borderRadius: "6px", maxWidth: "480px", width: "100%", padding: "40px", textAlign: "center", boxShadow: "0 24px 64px rgba(0,0,0,.25)" }}><div style={{ fontFamily: "'Playfair Display'", fontSize: "36px", color: C.gold, marginBottom: "8px" }}>◆</div><div style={{ fontFamily: "'Playfair Display'", fontSize: "22px", color: C.taupeDeep, marginBottom: "8px" }}>Valider l'Étape {etape.id}</div><div style={{ fontFamily: "'DM Sans'", fontSize: "13px", color: C.muted, marginBottom: "8px" }}>{etape.label}</div><div style={{ fontFamily: "'DM Sans'", fontSize: "12px", color: C.text, lineHeight: 1.7, marginBottom: "8px", padding: "16px", background: C.bgDeep, borderRadius: "3px" }}>
          En validant cette étape, vous confirmez que le livrable <strong style={{ color: C.taupeDeep }}>«{etape.livrable}»</strong> est finalisé et approuvé.
        </div><div style={{ fontFamily: "'DM Sans'", fontSize: "11px", color: C.alert, marginBottom: "24px", padding: "10px", background: C.alertLight, borderRadius: "3px" }}>
           Cette action débloquera l'étape suivante. La progression ne peut pas être annulée.
        </div><div style={{ display: "flex", gap: "12px", justifyContent: "center" }}><Btn onClick={onCancel} variant="ghost">
            Annuler
          </Btn><Btn onClick={onConfirm} variant="success">
            ✓ Confirmer la Validation
          </Btn></div></div></div>
  );
}

/* ─── ÉTAPES ───────────────────────────────────────── */

function Etape0({ data, set, missionName, isOPCMandate = false }) {
  const [loading, setLoading] = useState(false);
  const imgs = (data.plans || []).filter((p) => p.data).map((p) => p.data);

  const gen = async () => {
    setLoading(true);
    try {
      const sys = `Tu es l'OPC YOMA DESIGN. Rédige un Rapport d'Audit & Stratégie de chantier structuré : synthèse documentaire, incohérences, points critiques, écart budget, alertes et recommandations. Ton professionnel, concis.`;
      const user = `Mission : ${missionName || "—"}
Notes d'analyse : ${data.notesAnalyse || ""}
Incohérences détectées : ${data.incoherences || ""}
Points critiques : ${data.pointsCritiques || ""}
Budget vs réel : ${isOPCMandate ? "—" : (data.budgetVsReel || "")}`;
      const t = await callAI(sys, user, imgs);
      set((d) => ({ ...d, aiAudit: t }));
    } catch (e) {
      set((d) => ({ ...d, aiAudit: String(e.message || e) }));
    }
    setLoading(false);
  };

  return (
    <div className="step-content" style={{ display: "flex", flexDirection: "column", gap: "20px" }}><Card><SecTitle sub="Plans et analyse initiale">Audit & Stratégie</SecTitle><Grid cols={2}><div style={{ gridColumn: "1 / -1" }}><UploadZone label="Plans (PDF / DWG / PNG)" accept=".pdf,.dwg,.dxf,image/png,image/jpeg,application/pdf" files={data.plans} onFiles={(fn) => set((d) => ({ ...d, plans: fn(d.plans || []) }))} /></div><Field label="Notes d'analyse"><Input multiline rows={4} value={data.notesAnalyse || ""} onChange={(e) => set((d) => ({ ...d, notesAnalyse: e.target.value }))} placeholder="Contexte, hypothèses, méthodologie…" /></Field><Field label="Incohérences détectées"><Input multiline rows={4} value={data.incoherences || ""} onChange={(e) => set((d) => ({ ...d, incoherences: e.target.value }))} /></Field><Field label="Points critiques"><Input multiline rows={3} value={data.pointsCritiques || ""} onChange={(e) => set((d) => ({ ...d, pointsCritiques: e.target.value }))} /></Field>{!isOPCMandate && (
      <Field label="Budget vs réel"><Input multiline rows={3} value={data.budgetVsReel || ""} onChange={(e) => set((d) => ({ ...d, budgetVsReel: e.target.value }))} placeholder="Écarts, risques financiers…" /></Field>
      )}</Grid></Card><div style={{ textAlign: "center" }}><Btn onClick={gen} variant="gold" disabled={loading}>
          {loading ? "Génération…" : "Générer le Rapport d'Audit"}
        </Btn></div>
      {(data.aiAudit || loading) && <AIBox content={data.aiAudit} loading={loading} title="Rapport d'audit — IA" />}
      <LivrablerBadge code="AUD" label="Rapport d'analyse + alertes" generated={!!data.aiAudit && !loading} /></div>
  );
}

function Etape1({ data, set }) {
  const [loading, setLoading] = useState(false);
  const gen = async () => {
    setLoading(true);
    try {
      const sys = `Tu es l'OPC YOMA DESIGN. Produis un Dossier de Préparation Opérationnelle : planning officiel (jalons), tableau entreprises/lots, validation matériaux, plan d'installation chantier. Format structuré avec sections numérotées.`;
      const user = `Planning Gantt (dates par jalon) : ${data.ganttDates || ""}
Liste entreprises : ${data.listeEntreprises || ""}
Lots attribués : ${data.lotsAttribues || ""}
Validation matériaux : ${data.validationMateriaux || ""}
Plan installation chantier : ${data.planInstallation || ""}`;
      const t = await callAI(sys, user);
      set((d) => ({ ...d, aiPrep: t }));
    } catch (e) {
      set((d) => ({ ...d, aiPrep: String(e.message || e) }));
    }
    setLoading(false);
  };

  return (
    <div className="step-content" style={{ display: "flex", flexDirection: "column", gap: "20px" }}><Card><SecTitle sub="Organisation avant démarrage">Préparation opérationnelle</SecTitle><Grid cols={2}><Field label="Planning Gantt (dates par jalon)"><Input multiline rows={4} value={data.ganttDates || ""} onChange={(e) => set((d) => ({ ...d, ganttDates: e.target.value }))} placeholder="Jalon 1 : … | Jalon 2 : …" /></Field><Field label="Liste entreprises"><Input multiline rows={4} value={data.listeEntreprises || ""} onChange={(e) => set((d) => ({ ...d, listeEntreprises: e.target.value }))} /></Field><Field label="Lots attribués"><Input multiline rows={3} value={data.lotsAttribues || ""} onChange={(e) => set((d) => ({ ...d, lotsAttribues: e.target.value }))} /></Field><Field label="Validation matériaux"><Sel value={data.validationMateriaux || "non"} onChange={(e) => set((d) => ({ ...d, validationMateriaux: e.target.value }))} options={[{ value: "oui", label: "Oui" }, { value: "non", label: "Non" }]} /></Field><Field label="Plan installation chantier"><Input multiline rows={4} value={data.planInstallation || ""} onChange={(e) => set((d) => ({ ...d, planInstallation: e.target.value }))} /></Field></Grid></Card><div style={{ textAlign: "center" }}><Btn onClick={gen} variant="gold" disabled={loading}>
          {loading ? "Génération…" : "Générer le Dossier de Préparation"}
        </Btn></div>
      {(data.aiPrep || loading) && <AIBox content={data.aiPrep} loading={loading} title="Dossier de préparation — IA" />}
      <LivrablerBadge code="PREP" label="Planning officiel + Dossier technique" generated={!!data.aiPrep && !loading} /></div>
  );
}

function Etape2({ data, set }) {
  const [loading, setLoading] = useState(false);
  const gen = async () => {
    setLoading(true);
    try {
      const sys = `Tu es l'OPC YOMA DESIGN. Rédige un Compte-Rendu de Réunion de Lancement de chantier professionnel, prêt à être signé (structure formelle, participants, ordre du jour, décisions, règles chantier).`;
      const user = `Date réunion : ${data.dateLancement || ""}
Participants : ${data.participants || ""}
Points présentés : ${data.pointsPresentes || ""}
Règles chantier : ${data.reglesChantier || ""}`;
      const t = await callAI(sys, user);
      set((d) => ({ ...d, aiCrLancement: t }));
    } catch (e) {
      set((d) => ({ ...d, aiCrLancement: String(e.message || e) }));
    }
    setLoading(false);
  };

  return (
    <div className="step-content" style={{ display: "flex", flexDirection: "column", gap: "20px" }}><Card><SecTitle sub="Kick-off chantier">Lancement</SecTitle><Grid cols={2}><Field label="Date réunion de lancement"><Input type="date" value={data.dateLancement || ""} onChange={(e) => set((d) => ({ ...d, dateLancement: e.target.value }))} /></Field><Field label="Participants"><Input value={data.participants || ""} onChange={(e) => set((d) => ({ ...d, participants: e.target.value }))} placeholder="Noms, sociétés, rôles…" /></Field><Field label="Points présentés"><Input multiline rows={4} value={data.pointsPresentes || ""} onChange={(e) => set((d) => ({ ...d, pointsPresentes: e.target.value }))} /></Field><Field label="Règles de chantier"><Input multiline rows={4} value={data.reglesChantier || ""} onChange={(e) => set((d) => ({ ...d, reglesChantier: e.target.value }))} /></Field></Grid></Card><div style={{ textAlign: "center" }}><Btn onClick={gen} variant="gold" disabled={loading}>
          {loading ? "Génération…" : "Générer le CR de Réunion de Lancement"}
        </Btn></div>
      {(data.aiCrLancement || loading) && <AIBox content={data.aiCrLancement} loading={loading} title="CR réunion de lancement — IA" />}
      <LivrablerBadge code="CR-L" label="CR réunion signé" generated={!!data.aiCrLancement && !loading} /></div>
  );
}

function Etape3({ data, set, missionName }) {
  const [loading, setLoading] = useState(false);
  const visites = data.visites || [];
  const add = () => set((d) => ({ ...d, visites: [...(d.visites || []), { id: Date.now(), date: "", avancement: "", photos: [], blocages: "", comparatif: "" }] }));
  const up = (id, f, v) => set((d) => ({ ...d, visites: d.visites.map((i) => (i.id === id ? { ...i, [f]: v } : i)) }));
  const rm = (id) => set((d) => ({ ...d, visites: d.visites.filter((i) => i.id !== id) }));

  const gen = async () => {
    const v = visites[visites.length - 1];
    setLoading(true);
    try {
      const lastImgs = (v?.photos || []).map((p) => p.data).filter(Boolean);
      const sys = `Tu es l'OPC YOMA DESIGN. Rédige un Compte-Rendu de Visite hebdomadaire : date, avancement %, blocages, comparatif prévu/réel, recommandations, tableau de suivi synthétique.`;
      const user = `Mission : ${missionName || "—"}
Visite n°${visites.length} | Date : ${v?.date || ""}
Avancement constaté (%) : ${v?.avancement || ""}
Blocages : ${v?.blocages || ""}
Comparatif prévu / réel : ${v?.comparatif || ""}`;
      const t = await callAI(sys, user, lastImgs);
      set((d) => ({ ...d, aiCrVisite: t }));
    } catch (e) {
      set((d) => ({ ...d, aiCrVisite: String(e.message || e) }));
    }
    setLoading(false);
  };

  return (
    <div className="step-content" style={{ display: "flex", flexDirection: "column", gap: "20px" }}><Card><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}><SecTitle sub="Une visite = une fiche">Pilotage hebdomadaire</SecTitle><Btn onClick={add} small variant="ghost">
            + Visite
          </Btn></div>
        {visites.length === 0 && <div style={{ textAlign: "center", padding: "24px", color: C.muted, fontSize: "12px", fontStyle: "italic" }}>Ajoutez une fiche de visite</div>}
        {visites.map((v, idx) => (
          <div key={v.id} style={{ border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.gold}`, borderRadius: "3px", padding: "16px", background: C.bg, marginBottom: "12px", position: "relative" }}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}><span style={{ fontFamily: "'DM Sans'", fontSize: "11px", fontWeight: 600, color: C.taupe }}>Visite n° {idx + 1}</span><button type="button" onClick={() => rm(v.id)} style={{ background: "none", border: "none", cursor: "pointer", color: C.alert, fontSize: "14px" }}>
                ×
              </button></div><Grid cols={2}><Field label="Date visite"><Input type="date" value={v.date} onChange={(e) => up(v.id, "date", e.target.value)} /></Field><Field label="Avancement constaté (%)"><Input value={v.avancement} onChange={(e) => up(v.id, "avancement", e.target.value)} placeholder="ex. 65 %" /></Field></Grid><div style={{ marginTop: "12px" }}><UploadZone label="Photos" files={v.photos} onFiles={(fn) => set((d) => ({ ...d, visites: d.visites.map((i) => (i.id === v.id ? { ...i, photos: fn(i.photos || []) } : i)) }))} /></div><div style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "10px" }}><Field label="Blocages identifiés"><Input multiline rows={2} value={v.blocages} onChange={(e) => up(v.id, "blocages", e.target.value)} /></Field><Field label="Comparatif prévu / réel"><Input multiline rows={2} value={v.comparatif} onChange={(e) => up(v.id, "comparatif", e.target.value)} /></Field></div></div>
        ))}
      </Card>
      {visites.length > 0 && (
        <div style={{ textAlign: "center" }}><Btn onClick={gen} variant="gold" disabled={loading}>
            {loading ? "Génération…" : "Générer le CR de Visite"}
          </Btn></div>
      )}
      {(data.aiCrVisite || loading) && <AIBox content={data.aiCrVisite} loading={loading} title="CR de visite — IA" />}
      <LivrablerBadge code="CR-V" label="CR + tableau de suivi" generated={!!data.aiCrVisite && !loading} /></div>
  );
}

function Etape4({ data, set }) {
  const [loading, setLoading] = useState(false);
  const gen = async () => {
    setLoading(true);
    try {
      const sys = `Tu es l'OPC YOMA DESIGN. Rédige un Compte-Rendu de Coordination Technique : réunions OPC, interfaces, arbitrages, décisions actées et actions à suivre.`;
      const user = `Réunions OPC (dates, participants, décisions) : ${data.reunionsOpc || ""}
Interfaces techniques identifiées : ${data.interfacesTech || ""}
Arbitrages rendus : ${data.arbitrages || ""}`;
      const t = await callAI(sys, user);
      set((d) => ({ ...d, aiCoord: t }));
    } catch (e) {
      set((d) => ({ ...d, aiCoord: String(e.message || e) }));
    }
    setLoading(false);
  };

  return (
    <div className="step-content" style={{ display: "flex", flexDirection: "column", gap: "20px" }}><Card><SecTitle sub="Interfaces et décisions">Coordination technique</SecTitle><Field label="Réunions OPC (date, participants, décisions)"><Input multiline rows={5} value={data.reunionsOpc || ""} onChange={(e) => set((d) => ({ ...d, reunionsOpc: e.target.value }))} /></Field><Field label="Interfaces techniques identifiées"><Input multiline rows={4} value={data.interfacesTech || ""} onChange={(e) => set((d) => ({ ...d, interfacesTech: e.target.value }))} /></Field><Field label="Arbitrages rendus"><Input multiline rows={4} value={data.arbitrages || ""} onChange={(e) => set((d) => ({ ...d, arbitrages: e.target.value }))} /></Field></Card><div style={{ textAlign: "center" }}><Btn onClick={gen} variant="gold" disabled={loading}>
          {loading ? "Génération…" : "Générer le CR de Coordination"}
        </Btn></div>
      {(data.aiCoord || loading) && <AIBox content={data.aiCoord} loading={loading} title="CR coordination — IA" />}
      <LivrablerBadge code="CR-C" label="CR coordination + décisions actées" generated={!!data.aiCoord && !loading} /></div>
  );
}

function Etape5({ data, set, isOPCMandate = false }) {
  const [loading, setLoading] = useState(false);
  const rows = data.modifications || [];
  const add = () => set((d) => ({ ...d, modifications: [...(d.modifications || []), { id: Date.now(), demande: "", impactCout: "", impactDelai: "", statut: "En attente", osGenere: "non" }] }));
  const up = (id, f, v) => set((d) => ({ ...d, modifications: d.modifications.map((i) => (i.id === id ? { ...i, [f]: v } : i)) }));
  const rm = (id) => set((d) => ({ ...d, modifications: d.modifications.filter((i) => i.id !== id) }));

  const gen = async () => {
    setLoading(true);
    try {
      const sys = `Tu es l'OPC YOMA DESIGN. Rédige un Ordre de Service (OS) professionnel pour la dernière modification saisie : demande, impacts coût/délai, statut, référence traçabilité.`;
      const last = rows[rows.length - 1];
      const user = rows
        .map(
          (m, i) =>
            `#${i + 1} Demande : ${m.demande} | Coût MAD : ${isOPCMandate ? "—" : m.impactCout} | Délai (j) : ${m.impactDelai} | Statut : ${m.statut} | OS généré : ${m.osGenere}`
        )
        .join("\n");
      const t = await callAI(sys, `Dernière fiche prioritaire :\n${JSON.stringify(last || {})}\n\nToutes les fiches :\n${user}`);
      set((d) => ({ ...d, aiOS: t }));
    } catch (e) {
      set((d) => ({ ...d, aiOS: String(e.message || e) }));
    }
    setLoading(false);
  };

  return (
    <div className="step-content" style={{ display: "flex", flexDirection: "column", gap: "20px" }}><Card><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}><SecTitle sub="Modifications & OS">Gestion des modifications</SecTitle><Btn onClick={add} small variant="ghost">
            + Modification
          </Btn></div>
        {rows.length === 0 && <div style={{ textAlign: "center", padding: "24px", color: C.muted, fontSize: "12px", fontStyle: "italic" }}>Ajoutez une fiche de modification</div>}
        {rows.map((m, idx) => (
          <div key={m.id} style={{ border: `1px solid ${C.border}`, borderRadius: "3px", padding: "16px", background: C.bg, marginBottom: "12px" }}><div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}><span style={{ fontSize: "11px", fontWeight: 600, color: C.taupe }}>Modification {idx + 1}</span><button type="button" onClick={() => rm(m.id)} style={{ background: "none", border: "none", color: C.alert, cursor: "pointer" }}>
                ×
              </button></div><Field label="Demande client"><Input multiline rows={2} value={m.demande} onChange={(e) => up(m.id, "demande", e.target.value)} /></Field><Grid cols={isOPCMandate ? 1 : 2}>{!isOPCMandate && (
                <Field label="Impact coût (MAD)"><Input value={m.impactCout} onChange={(e) => up(m.id, "impactCout", e.target.value)} /></Field>
              )}<Field label="Impact délai (jours)"><Input value={m.impactDelai} onChange={(e) => up(m.id, "impactDelai", e.target.value)} /></Field><Field label="Statut"><Sel value={m.statut} onChange={(e) => up(m.id, "statut", e.target.value)} options={["En attente", "Validé", "Refusé"].map((x) => ({ value: x, label: x }))} /></Field><Field label="Ordre de service généré"><Sel value={m.osGenere || "non"} onChange={(e) => up(m.id, "osGenere", e.target.value)} options={[{ value: "oui", label: "Oui" }, { value: "non", label: "Non" }]} /></Field></Grid></div>
        ))}
      </Card>
      {rows.length > 0 && (
        <div style={{ textAlign: "center" }}><Btn onClick={gen} variant="gold" disabled={loading}>
            {loading ? "Génération…" : "Générer l'Ordre de Service"}
          </Btn></div>
      )}
      {(data.aiOS || loading) && <AIBox content={data.aiOS} loading={loading} title="Ordre de service — IA" />}
      <LivrablerBadge code="OS" label="OS validé + traçabilité" generated={!!data.aiOS && !loading} /></div>
  );
}

function Etape6({ data, set }) {
  const [loading, setLoading] = useState(false);
  const rows = data.retards || [];
  const add = () => set((d) => ({ ...d, retards: [...(d.retards || []), { id: Date.now(), entreprise: "", cause: "", impactJours: "", planCorrectif: "", statut: "Ouvert" }] }));
  const up = (id, f, v) => set((d) => ({ ...d, retards: d.retards.map((i) => (i.id === id ? { ...i, [f]: v } : i)) }));
  const rm = (id) => set((d) => ({ ...d, retards: d.retards.filter((i) => i.id !== id) }));

  const gen = async () => {
    setLoading(true);
    try {
      const sys = `Tu es l'OPC YOMA DESIGN. Rédige une Fiche Retard formelle : entreprise, cause, impact planning, plan correctif, statut, synthèse pour mise à jour du planning.`;
      const last = rows[rows.length - 1];
      const user = `Dernière fiche : ${JSON.stringify(last || {})}\n\nToutes :\n${rows.map((r, i) => `${i + 1}. ${r.entreprise} — ${r.cause} — ${r.impactJours} j — ${r.planCorrectif} — ${r.statut}`).join("\n")}`;
      const t = await callAI(sys, user);
      set((d) => ({ ...d, aiRetard: t }));
    } catch (e) {
      set((d) => ({ ...d, aiRetard: String(e.message || e) }));
    }
    setLoading(false);
  };

  return (
    <div className="step-content" style={{ display: "flex", flexDirection: "column", gap: "20px" }}><Card><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}><SecTitle sub="Suivi des dérives">Gestion des retards</SecTitle><Btn onClick={add} small variant="ghost">
            + Retard
          </Btn></div>
        {rows.length === 0 && <div style={{ textAlign: "center", padding: "24px", color: C.muted, fontSize: "12px", fontStyle: "italic" }}>Ajoutez une fiche retard</div>}
        {rows.map((r, idx) => (
          <div key={r.id} style={{ border: `1px solid ${C.border}`, borderRadius: "3px", padding: "16px", background: C.bg, marginBottom: "12px" }}><div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}><span style={{ fontSize: "11px", fontWeight: 600, color: C.taupe }}>Retard {idx + 1}</span><button type="button" onClick={() => rm(r.id)} style={{ background: "none", border: "none", color: C.alert, cursor: "pointer" }}>
                ×
              </button></div><Grid cols={2}><Field label="Entreprise concernée"><Input value={r.entreprise} onChange={(e) => up(r.id, "entreprise", e.target.value)} /></Field><Field label="Impact planning (jours)"><Input value={r.impactJours} onChange={(e) => up(r.id, "impactJours", e.target.value)} /></Field></Grid><Field label="Cause du retard"><Input multiline rows={2} value={r.cause} onChange={(e) => up(r.id, "cause", e.target.value)} /></Field><Field label="Plan correctif"><Input multiline rows={2} value={r.planCorrectif} onChange={(e) => up(r.id, "planCorrectif", e.target.value)} /></Field><Field label="Statut"><Sel value={r.statut} onChange={(e) => up(r.id, "statut", e.target.value)} options={["Ouvert", "Résolu"].map((x) => ({ value: x, label: x }))} /></Field></div>
        ))}
      </Card>
      {rows.length > 0 && (
        <div style={{ textAlign: "center" }}><Btn onClick={gen} variant="gold" disabled={loading}>
            {loading ? "Génération…" : "Générer la Fiche Retard"}
          </Btn></div>
      )}
      {(data.aiRetard || loading) && <AIBox content={data.aiRetard} loading={loading} title="Fiche retard — IA" />}
      <LivrablerBadge code="RET" label="Fiche retard + plan correctif" generated={!!data.aiRetard && !loading} /></div>
  );
}

function Etape7({ data, set }) {
  const [loading, setLoading] = useState(false);
  const zones = data.zones || [];
  const add = () => set((d) => ({ ...d, zones: [...(d.zones || []), { id: Date.now(), zone: "", reserves: "", photos: [], delaiCorrection: "" }] }));
  const up = (id, f, v) => set((d) => ({ ...d, zones: d.zones.map((i) => (i.id === id ? { ...i, [f]: v } : i)) }));
  const rm = (id) => set((d) => ({ ...d, zones: d.zones.filter((i) => i.id !== id) }));

  const gen = async () => {
    setLoading(true);
    try {
      const imgs = zones.flatMap((z) => (z.photos || []).map((p) => p.data)).filter(Boolean);
      const sys = `Tu es l'OPC YOMA DESIGN. Produis la Liste OPR complète : par zone/pièce, réserves, délais de correction, synthèse exécutive.`;
      const user = zones.map((z) => `Zone : ${z.zone}\nRéserves : ${z.reserves}\nDélai correction : ${z.delaiCorrection}`).join("\n\n---\n\n");
      const t = await callAI(sys, user, imgs.slice(0, 8));
      set((d) => ({ ...d, aiOpr: t }));
    } catch (e) {
      set((d) => ({ ...d, aiOpr: String(e.message || e) }));
    }
    setLoading(false);
  };

  return (
    <div className="step-content" style={{ display: "flex", flexDirection: "column", gap: "20px" }}><Card><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}><SecTitle sub="Inspection par zone / pièce">Pré-réception (OPR)</SecTitle><Btn onClick={add} small variant="ghost">
            + Zone
          </Btn></div>
        {zones.length === 0 && <div style={{ textAlign: "center", padding: "24px", color: C.muted, fontSize: "12px", fontStyle: "italic" }}>Ajoutez une ligne de checklist par zone</div>}
        {zones.map((z, idx) => (
          <div key={z.id} style={{ border: `1px solid ${C.border}`, borderRadius: "3px", padding: "16px", background: C.bg, marginBottom: "12px" }}><div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}><span style={{ fontSize: "11px", fontWeight: 600, color: C.taupe }}>Zone {idx + 1}</span><button type="button" onClick={() => rm(z.id)} style={{ background: "none", border: "none", color: C.alert, cursor: "pointer" }}>
                ×
              </button></div><Field label="Zone / pièce"><Input value={z.zone} onChange={(e) => up(z.id, "zone", e.target.value)} placeholder="ex. Salon, SDB RDC…" /></Field><Field label="Réserves constatées"><Input multiline rows={2} value={z.reserves} onChange={(e) => up(z.id, "reserves", e.target.value)} /></Field><UploadZone label="Photos" files={z.photos} onFiles={(fn) => set((d) => ({ ...d, zones: d.zones.map((i) => (i.id === z.id ? { ...i, photos: fn(i.photos || []) } : i)) }))} /><Field label="Délai correction"><Input value={z.delaiCorrection} onChange={(e) => up(z.id, "delaiCorrection", e.target.value)} placeholder="ex. 7 jours" /></Field></div>
        ))}
      </Card>
      {zones.length > 0 && (
        <div style={{ textAlign: "center" }}><Btn onClick={gen} variant="gold" disabled={loading}>
            {loading ? "Génération…" : "Générer la Liste OPR"}
          </Btn></div>
      )}
      {(data.aiOpr || loading) && <AIBox content={data.aiOpr} loading={loading} title="Liste OPR — IA" />}
      <LivrablerBadge code="OPR" label="Liste OPR complète" generated={!!data.aiOpr && !loading} /></div>
  );
}

function Etape8({ data, set }) {
  const [loading, setLoading] = useState(false);
  const gen = async () => {
    setLoading(true);
    try {
      const sys = `Tu es l'OPC YOMA DESIGN. Rédige un Procès-Verbal de Réception des travaux : date, parties, checklist levée des réserves OPR, réserves restantes, mentions légales type.`;
      const user = `Date réception : ${data.dateReception || ""}
Parties présentes : ${data.partiesPresentes || ""}
Checklist — levée des réserves OPR : ${data.checklistOpr || ""}
Réserves restantes : ${data.reservesRestantes || ""}`;
      const t = await callAI(sys, user);
      set((d) => ({ ...d, aiPv: t }));
    } catch (e) {
      set((d) => ({ ...d, aiPv: String(e.message || e) }));
    }
    setLoading(false);
  };

  return (
    <div className="step-content" style={{ display: "flex", flexDirection: "column", gap: "20px" }}><Card><SecTitle sub="PV et réserves">Réception</SecTitle><Grid cols={2}><Field label="Date réception"><Input type="date" value={data.dateReception || ""} onChange={(e) => set((d) => ({ ...d, dateReception: e.target.value }))} /></Field><Field label="Parties présentes"><Input value={data.partiesPresentes || ""} onChange={(e) => set((d) => ({ ...d, partiesPresentes: e.target.value }))} /></Field></Grid><Field label="Checklist finale — levée des réserves OPR"><Input multiline rows={4} value={data.checklistOpr || ""} onChange={(e) => set((d) => ({ ...d, checklistOpr: e.target.value }))} placeholder="Item par item, statut levée / en cours…" /></Field><Field label="Réserves restantes"><Input multiline rows={3} value={data.reservesRestantes || ""} onChange={(e) => set((d) => ({ ...d, reservesRestantes: e.target.value }))} /></Field></Card><div style={{ textAlign: "center" }}><Btn onClick={gen} variant="gold" disabled={loading}>
          {loading ? "Génération…" : "Générer le PV de Réception"}
        </Btn></div>
      {(data.aiPv || loading) && <AIBox content={data.aiPv} loading={loading} title="PV de réception — IA" />}
      <LivrablerBadge code="PV" label="PV de réception signé" generated={!!data.aiPv && !loading} /></div>
  );
}

function Etape9({ data, set }) {
  const [loading, setLoading] = useState(false);
  const gen = async () => {
    setLoading(true);
    try {
      const sys = `Tu es l'OPC YOMA DESIGN. Rédige la synthèse du DOE Final : état des livrables AS-BUILT, notices, garanties, GPA, observations finales et recommandations SAV.`;
      const user = `Plans AS-BUILT remis : ${data.plansAsBuilt}
Notices techniques remises : ${data.noticesTech}
Garanties fournisseurs remises : ${data.garanties}
GPA notifié : ${data.gpaNotifie}
Observations finales : ${data.observations || ""}`;
      const t = await callAI(sys, user);
      set((d) => ({ ...d, aiDoe: t }));
    } catch (e) {
      set((d) => ({ ...d, aiDoe: String(e.message || e) }));
    }
    setLoading(false);
  };

  return (
    <div className="step-content" style={{ display: "flex", flexDirection: "column", gap: "20px" }}><Card><SecTitle sub="Dossier des ouvrages exécutés">Clôture & SAV</SecTitle><Grid cols={2}><Field label="Plans AS-BUILT remis"><Sel value={data.plansAsBuilt || "non"} onChange={(e) => set((d) => ({ ...d, plansAsBuilt: e.target.value }))} options={[{ value: "oui", label: "Oui" }, { value: "non", label: "Non" }]} /></Field><Field label="Notices techniques remises"><Sel value={data.noticesTech || "non"} onChange={(e) => set((d) => ({ ...d, noticesTech: e.target.value }))} options={[{ value: "oui", label: "Oui" }, { value: "non", label: "Non" }]} /></Field><Field label="Garanties fournisseurs remises"><Sel value={data.garanties || "non"} onChange={(e) => set((d) => ({ ...d, garanties: e.target.value }))} options={[{ value: "oui", label: "Oui" }, { value: "non", label: "Non" }]} /></Field><Field label="GPA notifié"><Sel value={data.gpaNotifie || "non"} onChange={(e) => set((d) => ({ ...d, gpaNotifie: e.target.value }))} options={[{ value: "oui", label: "Oui" }, { value: "non", label: "Non" }]} /></Field></Grid><Field label="Observations finales"><Input multiline rows={4} value={data.observations || ""} onChange={(e) => set((d) => ({ ...d, observations: e.target.value }))} /></Field></Card><div style={{ textAlign: "center" }}><Btn onClick={gen} variant="gold" disabled={loading}>
          {loading ? "Génération…" : "Générer le DOE Final"}
        </Btn></div>
      {(data.aiDoe || loading) && <AIBox content={data.aiDoe} loading={loading} title="DOE final — IA" />}
      <LivrablerBadge code="DOE" label="DOE complet" generated={!!data.aiDoe && !loading} /></div>
  );
}

/* ══════════════════════════════════════════════════════
   MAIN
══════════════════════════════════════════════════════ */
export default function MissionOPC({ isOPCMandate = false }) {
  const [activeStep, setActiveStep] = useState(0);
  const [validated, setValidated] = useState({});
  const [showModal, setShowModal] = useState(null);
  const [stepsData, setStepsData] = useState({
    0: { missionName: "", plans: [], notesAnalyse: "", incoherences: "", pointsCritiques: "", budgetVsReel: "", aiAudit: "" },
    1: { ganttDates: "", listeEntreprises: "", lotsAttribues: "", validationMateriaux: "non", planInstallation: "", aiPrep: "" },
    2: { dateLancement: "", participants: "", pointsPresentes: "", reglesChantier: "", aiCrLancement: "" },
    3: { visites: [], aiCrVisite: "" },
    4: { reunionsOpc: "", interfacesTech: "", arbitrages: "", aiCoord: "" },
    5: { modifications: [], aiOS: "" },
    6: { retards: [], aiRetard: "" },
    7: { zones: [], aiOpr: "" },
    8: { dateReception: "", partiesPresentes: "", checklistOpr: "", reservesRestantes: "", aiPv: "" },
    9: { plansAsBuilt: "non", noticesTech: "non", garanties: "non", gpaNotifie: "non", observations: "", aiDoe: "" },
  });

  const setStep = (n) => (updater) => setStepsData((d) => ({ ...d, [n]: typeof updater === "function" ? updater(d[n]) : updater }));
  const isUnlocked = (id) => id === 0 || validated[id - 1];
  const isValidated = (id) => validated[id];

  const handleValidate = (id) => {
    setValidated((v) => ({ ...v, [id]: true }));
    setShowModal(null);
    if (id < 9) setTimeout(() => setActiveStep(id + 1), 300);
  };

  const currentEtape = ETAPES.find((e) => e.id === activeStep);
  const missionName = stepsData[0]?.missionName || "";

  return (
    <div style={{ minHeight: "100vh", background: C.bg }}><Styles />

      {showModal !== null && <ValidationModal etape={ETAPES.find((e) => e.id === showModal)} onConfirm={() => handleValidate(showModal)} onCancel={() => setShowModal(null)} />}

      <div style={{ background: C.taupeDeep, padding: "0 32px" }}><div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 0", borderBottom: `1px solid rgba(255,255,255,.08)` }}><div style={{ display: "flex", alignItems: "baseline", gap: "16px" }}><div style={{ fontFamily: "'Playfair Display'", fontSize: "20px", color: C.white, fontWeight: 400, letterSpacing: "0.12em" }}>YOMA DESIGN</div><div style={{ fontFamily: "'DM Sans'", fontSize: "9px", color: C.taupeLight, letterSpacing: "0.2em", textTransform: "uppercase" }}>Mission OPC — Ordonnancement, Pilotage, Coordination</div></div>
          {missionName ? (
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}><div style={{ fontFamily: "'Playfair Display'", fontSize: "14px", color: C.goldLight, fontStyle: "italic" }}>{missionName}</div><Badge color={C.goldLight}>OPC</Badge></div>
          ) : null}
        </div><div style={{ display: "flex", overflowX: "auto", paddingBottom: "0" }}>
          {ETAPES.map((e) => {
            const unlocked = isUnlocked(e.id);
            const validated_ = isValidated(e.id);
            const active = activeStep === e.id;
            return (
              <div
                key={e.id}
                role="button"
                tabIndex={0}
                onKeyDown={(ev) => ev.key === "Enter" && unlocked && setActiveStep(e.id)}
                onClick={() => unlocked && setActiveStep(e.id)}
                style={{
                  flex: "1 0 auto",
                  minWidth: "100px",
                  padding: "14px 8px",
                  cursor: unlocked ? "pointer" : "not-allowed",
                  borderBottom: active ? `3px solid ${C.gold}` : "3px solid transparent",
                  opacity: unlocked ? 1 : 0.4,
                  transition: "all .2s",
                  textAlign: "center",
                }}
              ><div style={{ fontSize: "14px", marginBottom: "4px", color: validated_ ? C.success : active ? C.gold : C.taupeLight }}>{validated_ ? "✓" : unlocked ? e.icon : "—"}</div><div style={{ fontFamily: "'DM Sans'", fontSize: "9px", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: active ? C.white : validated_ ? C.success : C.taupeLight }}>{e.short}</div><div style={{ fontFamily: "'DM Sans'", fontSize: "8px", color: C.taupeLight, marginTop: "2px", opacity: 0.7 }}>
                  Étape {e.id}
                </div></div>
            );
          })}
        </div></div><div style={{ maxWidth: "960px", margin: "0 auto", padding: "32px 24px" }}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "28px" }}><div><div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "6px" }}><span style={{ fontFamily: "'Playfair Display'", fontSize: "28px", color: currentEtape.color, fontWeight: 400 }}>{currentEtape.icon}</span><div><div style={{ fontFamily: "'Playfair Display'", fontSize: "26px", color: C.taupeDeep, fontWeight: 500, lineHeight: 1.1 }}>{currentEtape.label}</div><div style={{ fontFamily: "'DM Sans'", fontSize: "11px", color: C.muted, marginTop: "4px" }}>{currentEtape.subtitle}</div></div></div><div style={{ fontFamily: "'DM Sans'", fontSize: "12px", color: C.muted, maxWidth: "520px", lineHeight: 1.6 }}>{currentEtape.description}</div></div><div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "8px" }}><Badge color={currentEtape.color}>
              Étape {activeStep} · {ETAPES.length} phases
            </Badge>
            {isValidated(activeStep) && <Badge color={C.success}>✓ Validée</Badge>}
          </div></div>

        {activeStep === 0 && (
          <Card style={{ marginBottom: "20px" }}><Field label="Nom de la mission (affiché dans l'en-tête)" required><Input value={stepsData[0].missionName} onChange={(e) => setStep(0)((d) => ({ ...d, missionName: e.target.value }))} placeholder="ex. Villa Palmeraie — OPC" /></Field></Card>
        )}

        {!isUnlocked(activeStep) && (
          <div style={{ textAlign: "center", padding: "64px 32px", background: C.lockedBg, border: `2px dashed ${C.border}`, borderRadius: "6px" }}><div style={{ fontFamily: "'Playfair Display'", fontSize: "48px", color: C.locked, marginBottom: "16px" }}>—</div><div style={{ fontFamily: "'Playfair Display'", fontSize: "22px", color: C.muted, marginBottom: "8px" }}>Étape verrouillée</div><div style={{ fontFamily: "'DM Sans'", fontSize: "13px", color: C.muted, maxWidth: "360px", margin: "0 auto", lineHeight: 1.7 }}>
              Cette étape sera débloquée après validation de l'étape précédente — <strong>«{ETAPES.find((e) => e.id === activeStep - 1)?.label}»</strong></div></div>
        )}

        {isValidated(activeStep) && isUnlocked(activeStep) && (
          <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "14px 20px", background: C.successLight, border: `1px solid ${C.success}40`, borderRadius: "3px", marginBottom: "20px" }}><span style={{ fontSize: "18px" }}>✓</span><div><div style={{ fontFamily: "'DM Sans'", fontSize: "12px", fontWeight: 600, color: C.success }}>
                Étape validée — Livrable «{currentEtape.livrable}» confirmé
              </div><div style={{ fontFamily: "'DM Sans'", fontSize: "11px", color: C.muted }}>Vous pouvez consulter le contenu mais la progression est verrouillée.</div></div></div>
        )}

        {isUnlocked(activeStep) && (
          <>
            {activeStep === 0 && <Etape0 data={stepsData[0]} set={setStep(0)} missionName={missionName} isOPCMandate={isOPCMandate} />}
            {activeStep === 1 && <Etape1 data={stepsData[1]} set={setStep(1)} />}
            {activeStep === 2 && <Etape2 data={stepsData[2]} set={setStep(2)} />}
            {activeStep === 3 && <Etape3 data={stepsData[3]} set={setStep(3)} missionName={missionName} />}
            {activeStep === 4 && <Etape4 data={stepsData[4]} set={setStep(4)} />}
            {activeStep === 5 && <Etape5 data={stepsData[5]} set={setStep(5)} isOPCMandate={isOPCMandate} />}
            {activeStep === 6 && <Etape6 data={stepsData[6]} set={setStep(6)} />}
            {activeStep === 7 && <Etape7 data={stepsData[7]} set={setStep(7)} />}
            {activeStep === 8 && <Etape8 data={stepsData[8]} set={setStep(8)} />}
            {activeStep === 9 && <Etape9 data={stepsData[9]} set={setStep(9)} />}
          </>
        )}

        {isUnlocked(activeStep) && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "36px", paddingTop: "24px", borderTop: `1px solid ${C.border}` }}><Btn onClick={() => setActiveStep((s) => Math.max(0, s - 1))} variant="ghost" disabled={activeStep === 0}>
              ← Étape précédente
            </Btn><div style={{ display: "flex", gap: "12px" }}>
              {!isValidated(activeStep) && (
                <Btn onClick={() => setShowModal(activeStep)} variant="success" style={{ padding: "12px 28px", fontSize: "11px" }}>
                  ✓ {currentEtape.validationLabel}
                </Btn>
              )}
              {activeStep < 9 && isValidated(activeStep) && (
                <Btn onClick={() => setActiveStep((s) => Math.min(9, s + 1))} variant="primary">
                  Étape suivante →
                </Btn>
              )}
            </div></div>
        )}
      </div></div>
  );
}
